// src/i18n/index.ts
// i18next 单例。app 入口副作用 import 一次。
// 仅首屏必需的 en namespace（common=默认/兜底，nav=全局侧边栏）内联主包；
// 其余 en namespace 与全部 zh/pt/es，按 locale×namespace 经 Vite dynamic import 懒加载。
// （内联整个 en 目录会把 ~470KB 翻译塞进入口 chunk，其中 tasks.json 单文件就 ~97KB，
//   是首屏体积回归的主因——故此处只内联白名单，大块按需加载。）
import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'
import resourcesToBackend from 'i18next-resources-to-backend'
import dayjs from 'dayjs'
import 'dayjs/locale/zh-cn'
import 'dayjs/locale/pt-br'
import 'dayjs/locale/es'
import {
  DAYJS_LOCALES, LOCALE_STORAGE_KEY, SOURCE_LOCALE, SUPPORTED_LOCALES,
  isAppLocale, resolveInitialLocale,
} from './locales'
import { reloadOnce } from '@/utils/chunkReload'

// 首屏内联白名单：只 eager-import 这几个 en namespace；新增首屏必需的 namespace 时往这里加。
// 其余 namespace 由下方 resourcesToBackend 在组件 useTranslation(ns) 时按需 import。
// auth 必须内联：login 页对未登录用户就是首屏,懒加载时 useSuspense:false 会先渲染出
// raw key(en 自己的懒 ns 没有 fallback 文本可显),冷启动 dev/慢网下窗口长达数秒。
// listPage 同理且更普遍：filterBar.* / viewPanel.* / column.* 是**所有列表页共用的外壳**
// (30+ 个路由),懒加载窗口里整条工具栏会渲染成 "filterBar.refresh" 这种键名 ——
// dev 上实测到过(2026-07-31,截图见 E2E test-results)。12.9KB,换掉全站列表页的 raw key 值得。
const enModules = import.meta.glob('../locales/en/{common,nav,auth,listPage}.json', { eager: true }) as Record<
  string, { default: Record<string, unknown> }
>
const enResources: Record<string, Record<string, unknown>> = {}
for (const [filePath, mod] of Object.entries(enModules)) {
  const ns = filePath.match(/\/en\/(.+)\.json$/)?.[1]
  if (ns) enResources[ns] = mod.default
}

// 加载失败过的 namespace（见下方 failedLoading 监听）
const failedNamespaces = new Set<string>()

const initialLng = resolveInitialLocale(
  typeof localStorage !== 'undefined' ? localStorage.getItem(LOCALE_STORAGE_KEY) : null,
  typeof navigator !== 'undefined' ? navigator.language : undefined,
)

void i18n
  .use(resourcesToBackend((lng: string, ns: string) => import(`../locales/${lng}/${ns}.json`)))
  .use(initReactI18next)
  .init({
    lng: initialLng,
    fallbackLng: SOURCE_LOCALE,
    supportedLngs: [...SUPPORTED_LOCALES],
    ns: Object.keys(enResources),
    defaultNS: 'common',
    fallbackNS: 'common',
    resources: { en: enResources },
    partialBundledLanguages: true, // en 内联 + 其余走 backend 的关键开关
    interpolation: { escapeValue: false }, // React 自带转义
    returnNull: false,
    react: { useSuspense: false }, // 懒加载期间先渲染，加载完自动重渲染（见下方 parseMissingKeyHandler）

    // ── 懒加载窗口不许把 raw key 渲染给用户 ──────────────────────────────
    // useSuspense:false 意味着 namespace 还在下载时组件照常渲染，而**未内联的 en
    // namespace 没有任何 fallback 文本**，t() 只能吐键名 —— dev 上实测到过整页
    // "filterBar.refresh" / "page.searchPlaceholder" / "COLUMN.PROPERTY"
    // (2026-07-31，截图见 E2E test-results)。
    //
    // 这里按 namespace 是否已加载分流：
    //   还没加载完 → 返回空串，先留白，加载完 i18next 自动重渲染出真文案；
    //   已加载但键缺失 → 保留键名，漏翻译照样一眼看得见（不掩盖真问题）。
    //   加载失败了 → 也保留键名：失败不会重试，返回空串就是永久留白。
    // appendNamespaceToMissingKey 是为了让 handler 能拿到 ns（默认只给键名）。
    appendNamespaceToMissingKey: true,
    parseMissingKeyHandler: (key: string, defaultValue?: string) => {
      // ⚠️ 这个 handler 的返回值会**盖过**调用方传的 defaultValue（i18next 的优先级），
      // 所以必须先把它还回去：仓库里大量 `t(k, { defaultValue: '' }) || humanize(id)`
      // 的兜底写法全靠它（capability 标签、task 优先级信号等），抢了就把人家打坏。
      if (defaultValue !== undefined) return defaultValue
      const idx = key.indexOf(':')
      const ns = idx > 0 ? key.slice(0, idx) : (i18n.options.defaultNS as string)
      const bareKey = idx > 0 ? key.slice(idx + 1) : key
      const loaded =
        i18n.hasResourceBundle(i18n.language, ns) || i18n.hasResourceBundle(SOURCE_LOCALE, ns)
      return loaded || failedNamespaces.has(ns) ? bareKey : ''
    },
  })

// 翻译包懒加载失败：几乎都是发版后旧 chunk hash 404，i18next 不重试。
// 不处理的话这个 namespace 的文案永远是空串——整个对话框只剩空框（2026-10-15 AI 助手「加入评测」）。
// 和路由懒加载失败一样自动刷新一次拿新 bundle；冷却期内再失败就不刷，改显示键名。
i18n.on('failedLoading', (_lng: string, ns: string, err: unknown) => {
  console.error(`[i18n] failed to load namespace "${ns}"`, err)
  failedNamespaces.add(ns)
  reloadOnce()
})

// 语言切换副作用：dayjs 全局 locale + <html lang>
i18n.on('languageChanged', (lng) => {
  dayjs.locale(isAppLocale(lng) ? DAYJS_LOCALES[lng] : 'en')
  if (typeof document !== 'undefined') document.documentElement.lang = lng
})
dayjs.locale(isAppLocale(initialLng) ? DAYJS_LOCALES[initialLng] : 'en')

export default i18n
